// src/pages/interfaces/InterfaceDetailPage.tsx

import {
  Activity,
  AlertCircle,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Clock,
  Gauge,
  Loader2,
  RefreshCw,
  Signal,
  TrendingUp,
  Wifi,
  X,
  Zap,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { fetchApp } from "../../services/apiService";

interface TrafficSample {
  interface: string;
  timestamp: string;
  rx_bps: number;
  tx_bps: number;
}

interface ChartPoint extends TrafficSample {
  time: string;
  rx_label: string;
  tx_label: string;
}

const MAX_POINTS = 60;

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const formatBps = (bps: number): string => {
  const n = Number(bps);

  if (!Number.isFinite(n) || n <= 0) return "0 B/s";

  const units = ["B/s", "KB/s", "MB/s", "GB/s", "TB/s"];
  const k = 1024;
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(k)));
  const value = n / Math.pow(k, i);
  const decimals = value >= 100 ? 0 : value >= 10 ? 1 : 2;

  return `${value.toFixed(decimals)} ${units[i]}`;
};

const formatTime = (value?: string | null) => {
  if (!value) return "--:--:--";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "--:--:--";

  return date.toLocaleTimeString("en-KE", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const LoadingState: React.FC = () => (
  <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
    <div className="mx-auto flex min-h-[60vh] max-w-[1600px] items-center justify-center">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

        <div className="relative overflow-hidden px-6 py-10 text-center">
          <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-emerald-200/35 blur-2xl" />
          <div className="absolute -bottom-20 left-4 h-52 w-52 rounded-full bg-cyan-200/35 blur-2xl" />

          <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/50">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>

          <p className="relative mt-4 text-[15px] font-black text-slate-900">
            Loading interface traffic…
          </p>

          <p className="relative mt-1 text-[12.5px] font-medium text-slate-500">
            Please wait while real-time traffic samples are being prepared.
          </p>

          <div className="relative mt-6 flex justify-center gap-1.5">
            <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
            <span className="h-2 w-2 animate-pulse rounded-full bg-teal-500 delay-150" />
            <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-500 delay-300" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const SmallMetric: React.FC<{
  label: string;
  value: string;
  icon: React.ReactNode;
  tone: "emerald" | "sky" | "amber" | "slate";
}> = ({ label, value, icon, tone }) => {
  const styles = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    sky: "border-sky-200 bg-sky-50 text-sky-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    slate: "border-slate-200 bg-slate-100 text-slate-700",
  }[tone];

  return (
    <div className={`rounded-3xl border px-4 py-4 ${styles}`}>
      <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest opacity-80">
        {icon}
        {label}
      </div>

      <p className="mt-2 font-mono text-[18px] font-black text-slate-900">
        {value}
      </p>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || payload.length === 0) return null;

  const rx = payload.find((item: any) => item.dataKey === "rx_bps");
  const tx = payload.find((item: any) => item.dataKey === "tx_bps");

  return (
    <div className="rounded-2xl border border-slate-700 bg-slate-950/95 px-4 py-3 shadow-xl">
      <p className="mb-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
        {label}
      </p>

      <div className="space-y-1.5">
        <p className="flex items-center justify-between gap-5 text-[12px] font-bold text-emerald-300">
          <span>RX</span>
          <span className="font-mono">{formatBps(Number(rx?.value || 0))}</span>
        </p>

        <p className="flex items-center justify-between gap-5 text-[12px] font-bold text-sky-300">
          <span>TX</span>
          <span className="font-mono">{formatBps(Number(tx?.value || 0))}</span>
        </p>
      </div>
    </div>
  );
};

const InterfaceDetailPage: React.FC = () => {
  const { routerId, name } = useParams<{ routerId: string; name: string }>();
  const navigate = useNavigate();

  const ifaceName = decodeURIComponent(name || "");

  const [trafficHistory, setTrafficHistory] = useState<TrafficSample[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTraffic = useCallback(
    async (manual = false) => {
      if (!routerId || !ifaceName) return;

      try {
        if (manual) setRefreshing(true);

        const sample = await fetchApp<TrafficSample>(
          `/interface/traffic?router_id=${encodeURIComponent(
            routerId,
          )}&name=${encodeURIComponent(ifaceName)}`,
        );

        setTrafficHistory((prev) => {
          const next = [...prev, sample];
          return next.slice(-MAX_POINTS);
        });

        setError(null);
      } catch (e: any) {
        setError(e?.message || "Failed to load interface traffic.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [routerId, ifaceName],
  );

  useEffect(() => {
    if (!routerId || !ifaceName) {
      setLoading(false);
      setError("Missing router or interface name.");
      return;
    }

    let cancelled = false;

    const run = async () => {
      if (!cancelled) await fetchTraffic(false);
    };

    run();

    const intervalId = window.setInterval(() => {
      if (!cancelled) {
        fetchTraffic(false);
      }
    }, 2000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [routerId, ifaceName, fetchTraffic]);

  const latest = trafficHistory[trafficHistory.length - 1];

  const stats = useMemo(() => {
    if (trafficHistory.length === 0) {
      return {
        peakRx: 0,
        peakTx: 0,
        avgRx: 0,
        avgTx: 0,
        totalRx: 0,
        totalTx: 0,
      };
    }

    let peakRx = 0;
    let peakTx = 0;
    let totalRx = 0;
    let totalTx = 0;

    trafficHistory.forEach((sample) => {
      peakRx = Math.max(peakRx, Number(sample.rx_bps || 0));
      peakTx = Math.max(peakTx, Number(sample.tx_bps || 0));
      totalRx += Number(sample.rx_bps || 0);
      totalTx += Number(sample.tx_bps || 0);
    });

    return {
      peakRx,
      peakTx,
      avgRx: totalRx / trafficHistory.length,
      avgTx: totalTx / trafficHistory.length,
      totalRx,
      totalTx,
    };
  }, [trafficHistory]);

  const chartData = useMemo<ChartPoint[]>(() => {
    return trafficHistory.map((sample) => ({
      ...sample,
      time: formatTime(sample.timestamp),
      rx_label: formatBps(sample.rx_bps),
      tx_label: formatBps(sample.tx_bps),
    }));
  }, [trafficHistory]);

  const isLive = Boolean(latest && !error);

  if (loading && trafficHistory.length === 0) {
    return <LoadingState />;
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="mb-3 inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-[12px] font-black text-slate-600 shadow-sm transition hover:bg-slate-100 hover:text-slate-900"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Interfaces
                </button>

                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      isLive ? "animate-pulse bg-emerald-500" : "bg-slate-400"
                    }`}
                  />
                  {isLive
                    ? "Live Interface Monitoring"
                    : "Interface Monitoring"}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <h1 className="max-w-full truncate text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[26px]">
                    {ifaceName || "Unknown Interface"}
                  </h1>

                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-black ${
                      isLive
                        ? "border-emerald-200 bg-emerald-100 text-emerald-800"
                        : "border-slate-300 bg-slate-200 text-slate-600"
                    }`}
                  >
                    <Wifi className="h-3 w-3" />
                    {isLive ? "Live" : "Waiting"}
                  </span>
                </div>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  Real-time RX and TX traffic monitoring with rolling samples,
                  peak usage, and average throughput.
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-4 text-[12px] font-black text-slate-600 shadow-sm">
                  <Clock className="h-4 w-4 text-slate-400" />
                  {latest ? formatTime(latest.timestamp) : "--:--:--"}
                </div>

                <button
                  type="button"
                  onClick={() => fetchTraffic(true)}
                  disabled={refreshing}
                  className={`${btnBase} h-10 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  {refreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {refreshing ? "Refreshing…" : "Refresh"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {refreshing && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing interface traffic sample…
          </div>
        )}

        {error && (
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={() => setError(null)}
              className="rounded-xl p-1.5 text-rose-600 transition-colors hover:bg-rose-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 xl:gap-4">
          <StatCard
            label="Current RX"
            value={latest ? formatBps(latest.rx_bps) : "0 B/s"}
            sub="Download rate"
            icon={<ArrowDown className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Current TX"
            value={latest ? formatBps(latest.tx_bps) : "0 B/s"}
            sub="Upload rate"
            icon={<ArrowUp className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="Peak RX"
            value={formatBps(stats.peakRx)}
            sub="Highest received rate"
            icon={<Zap className="h-5 w-5" />}
            tone="amber"
          />

          <StatCard
            label="Peak TX"
            value={formatBps(stats.peakTx)}
            sub="Highest transmitted rate"
            icon={<TrendingUp className="h-5 w-5" />}
            tone="slate"
          />
        </div>

        {/* Chart */}
        <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-950 shadow-xl shadow-slate-400/25">
          <div className="flex flex-col gap-3 border-b border-slate-800 bg-slate-900 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-300">
                <Activity className="h-5 w-5" />
              </div>

              <div>
                <p className="text-[14px] font-black text-slate-50">
                  Real-time Traffic Chart
                </p>

                <p className="text-[11.5px] font-medium text-slate-400">
                  Rolling window of the last {MAX_POINTS} samples
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-black text-emerald-300">
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                RX
              </span>

              <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-500/30 bg-sky-500/10 px-2.5 py-1 text-[11px] font-black text-sky-300">
                <span className="h-2 w-2 rounded-full bg-sky-400" />
                TX
              </span>

              <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 bg-slate-800 px-2.5 py-1 text-[11px] font-black text-slate-300">
                <Signal className="h-3 w-3" />
                {trafficHistory.length} samples
              </span>
            </div>
          </div>

          <div className="p-3 sm:p-5">
            {chartData.length > 0 ? (
              <div className="h-[360px] w-full sm:h-[430px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={chartData}
                    margin={{
                      top: 12,
                      right: 18,
                      left: 0,
                      bottom: 8,
                    }}
                  >
                    <defs>
                      <linearGradient
                        id="rxGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#10b981"
                          stopOpacity={0.45}
                        />
                        <stop
                          offset="95%"
                          stopColor="#10b981"
                          stopOpacity={0.03}
                        />
                      </linearGradient>

                      <linearGradient
                        id="txGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#38bdf8"
                          stopOpacity={0.4}
                        />
                        <stop
                          offset="95%"
                          stopColor="#38bdf8"
                          stopOpacity={0.03}
                        />
                      </linearGradient>
                    </defs>

                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#334155"
                      opacity={0.65}
                    />

                    <XAxis
                      dataKey="time"
                      stroke="#94a3b8"
                      tick={{ fontSize: 11, fill: "#94a3b8" }}
                      tickMargin={10}
                      minTickGap={28}
                    />

                    <YAxis
                      stroke="#94a3b8"
                      tick={{ fontSize: 11, fill: "#94a3b8" }}
                      tickFormatter={(value) => formatBps(Number(value))}
                      width={80}
                    />

                    <Tooltip content={<CustomTooltip />} />

                    <Legend
                      verticalAlign="top"
                      height={36}
                      formatter={(value) => {
                        if (value === "rx_bps") return "RX Download";
                        if (value === "tx_bps") return "TX Upload";
                        return value;
                      }}
                    />

                    <Area
                      type="monotone"
                      dataKey="rx_bps"
                      stroke="#10b981"
                      strokeWidth={2.5}
                      fill="url(#rxGradient)"
                      dot={false}
                      activeDot={{ r: 4 }}
                      isAnimationActive={false}
                    />

                    <Area
                      type="monotone"
                      dataKey="tx_bps"
                      stroke="#38bdf8"
                      strokeWidth={2.5}
                      fill="url(#txGradient)"
                      dot={false}
                      activeDot={{ r: 4 }}
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex min-h-[340px] flex-col items-center justify-center rounded-3xl border border-slate-800 bg-slate-900/60 px-4 text-center">
                <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-800">
                  <Gauge className="h-7 w-7 text-slate-400" />
                </div>

                <p className="text-[14px] font-black text-slate-100">
                  Waiting for traffic samples
                </p>

                <p className="mt-1 max-w-sm text-[13px] font-medium text-slate-400">
                  The chart will appear once the interface starts returning RX
                  and TX traffic data.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Lower Metrics */}
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
          <SmallMetric
            label="Average RX"
            value={formatBps(stats.avgRx)}
            icon={<ArrowDown className="h-3.5 w-3.5" />}
            tone="emerald"
          />

          <SmallMetric
            label="Average TX"
            value={formatBps(stats.avgTx)}
            icon={<ArrowUp className="h-3.5 w-3.5" />}
            tone="sky"
          />

          <SmallMetric
            label="Samples"
            value={String(trafficHistory.length)}
            icon={<Activity className="h-3.5 w-3.5" />}
            tone="amber"
          />

          <SmallMetric
            label="Last Update"
            value={latest ? formatTime(latest.timestamp) : "--:--:--"}
            icon={<Clock className="h-3.5 w-3.5" />}
            tone="slate"
          />
        </div>
      </div>
    </div>
  );
};

export default InterfaceDetailPage;
